import type { TimeRange, Timestamp } from "@/types/date"
import type {
  ItineraryEvent,
  ItineraryEventCreation,
  ItineraryEventProperties,
  ItineraryEventType,
  ItineraryEventUpdates,
} from "@/types/itinerary"
import type { Place } from "@/types/place"
import type { TravelerGroup } from "@/types/trip"

import { addDays, getDayObject } from "@/lib/date-utils"
import type {
  AddCustomItineraryEventActionInput,
  AddPlaceItineraryEventActionInput,
  UpdateItineraryEventActionInput,
} from "@/lib/itinerary-action-types"
import {
  defaultActivityEndTime,
  defaultHotelCheckOutTime,
  defaultStartAndEndTimeForCustom,
  defaultTimeRange,
} from "@/lib/itinerary-utils"
import { assertPresent } from "@/lib/utils/assertions"
import { present } from "@/lib/utils/presence"

export interface PreparedAddItineraryEventValues {
  place?: Place
  endPlace?: Place
  start: Timestamp
  end: Timestamp
  allDay: boolean
  timeZone?: string
  endTimeZone?: string
  travelers?: TravelerGroup
  type?: ItineraryEventType
  title?: string
  user_note?: string
  properties?: ItineraryEventProperties
  messageId?: number
  webSearchResultId?: string
}

export interface PreparedAddItineraryEventAction {
  next: PreparedAddItineraryEventValues
  scheduled?: boolean
  updateTripTimeFrame?: boolean
}

export interface PreparedUpdateItineraryEventValues {
  place?: Place
  endPlace?: Place
  start: Timestamp
  end: Timestamp
  allDay: boolean
  timeZone: string
  endTimeZone: string
  travelers: TravelerGroup
  type: ItineraryEventType
  title: string
  user_note?: string
  properties?: ItineraryEventProperties
}

export interface PreparedUpdateItineraryEventAction {
  event: ItineraryEvent
  previous: PreparedUpdateItineraryEventValues & {
    scheduled: boolean
  }
  next: PreparedUpdateItineraryEventValues
  requestedScheduled?: boolean
  updateTripTimeFrame?: boolean
}

export interface CreateItineraryEventParamsOptions {
  updateTimeFrame?: boolean
}

export interface UpdateItineraryEventParamsOptions {
  updateTimeFrame?: boolean
}

export function prepareAddPlaceItineraryEventAction(
  addProps: AddPlaceItineraryEventActionInput
): PreparedAddItineraryEventAction {
  const defaultTime = addProps.defaultTime != null ? addProps.defaultTime : true
  const allDay = addProps.allDay != null ? addProps.allDay : false
  const timeRange: TimeRange = defaultTime
    ? defaultTimeRange(
        addProps.place,
        getDayObject(addProps.start, addProps.place.time_zone),
        addProps.end && getDayObject(addProps.end, addProps.place.time_zone)
      )
    : { from: addProps.start, to: addProps.end }

  return {
    next: {
      place: addProps.place,
      start: timeRange.from ?? addProps.start,
      end: assertPresent(timeRange.to),
      allDay,
      travelers: addProps.travelers,
    },
    scheduled: addProps.scheduled,
    updateTripTimeFrame: addProps.updateTripTimeFrame,
  }
}

export function prepareAddCustomItineraryEventAction(
  addCustomProps: AddCustomItineraryEventActionInput
): PreparedAddItineraryEventAction {
  const defaultTime =
    addCustomProps.defaultTime != null ? addCustomProps.defaultTime : true
  const allDay = addCustomProps.allDay != null ? addCustomProps.allDay : false
  const timeRange: TimeRange = defaultTime
    ? defaultStartAndEndTimeForCustom(
        getDayObject(addCustomProps.start, addCustomProps.timeZone),
        addCustomProps.end &&
          getDayObject(addCustomProps.end, addCustomProps.timeZone),
        addCustomProps.timeZone
      )
    : { from: addCustomProps.start, to: addCustomProps.end }

  return {
    next: {
      place: addCustomProps.place,
      endPlace: addCustomProps.endPlace,
      type: addCustomProps.type,
      title: addCustomProps.title,
      user_note: addCustomProps.user_note,
      travelers: addCustomProps.travelers,
      properties: addCustomProps.properties,
      start: timeRange.from ?? addCustomProps.start,
      end: assertPresent(timeRange.to),
      allDay,
      timeZone: addCustomProps.timeZone,
      endTimeZone: addCustomProps.endTimeZone,
      messageId: addCustomProps.messageId,
      webSearchResultId: addCustomProps.webSearchResultId,
    },
    scheduled: addCustomProps.scheduled,
    updateTripTimeFrame: addCustomProps.updateTripTimeFrame,
  }
}

export function buildCreateItineraryEventParams(
  addAction: PreparedAddItineraryEventAction,
  options: CreateItineraryEventParamsOptions = {}
): ItineraryEventCreation {
  const next = addAction.next

  return {
    entity_ref: next.place?.entity_ref,
    end_entity_ref: next.endPlace?.entity_ref,
    type: next.type,
    title: next.title,
    user_note: next.user_note,
    properties: next.properties,
    start_at: next.start,
    end_at: next.end,
    all_day: next.allDay,
    time_zone: next.timeZone,
    end_time_zone: next.endTimeZone,
    travelers: next.travelers,
    update_time_frame: options.updateTimeFrame,
    scheduled: addAction.scheduled,
    message_id: next.messageId,
    web_search_result_id: next.webSearchResultId,
  }
}

export function buildUpdateItineraryEventParams(
  updateAction: PreparedUpdateItineraryEventAction,
  options: UpdateItineraryEventParamsOptions = {}
): ItineraryEventUpdates {
  const next = updateAction.next

  return {
    entity_ref: next.place?.entity_ref,
    end_entity_ref: next.endPlace?.entity_ref,
    start_at: next.start,
    end_at: next.end,
    all_day: next.allDay,
    time_zone: next.timeZone,
    end_time_zone: next.endTimeZone,
    title: next.title,
    user_note: next.user_note,
    travelers: next.travelers,
    properties: next.properties,
    type: next.type,
    update_time_frame: options.updateTimeFrame,
    scheduled: updateAction.requestedScheduled,
  }
}

export function isItineraryMoveOnlyUpdate(
  updateAction: PreparedUpdateItineraryEventAction
): boolean {
  const { previous, next, requestedScheduled } = updateAction

  const nextScheduled =
    requestedScheduled !== undefined ? requestedScheduled : previous.scheduled

  if (nextScheduled !== true || previous.scheduled !== true) {
    return false
  }

  if (
    previous.place?.entity_ref !== next.place?.entity_ref ||
    previous.endPlace?.entity_ref !== next.endPlace?.entity_ref ||
    previous.type !== next.type ||
    previous.title !== next.title ||
    previous.user_note !== next.user_note ||
    JSON.stringify(previous.travelers) !== JSON.stringify(next.travelers) ||
    JSON.stringify(previous.properties) !== JSON.stringify(next.properties)
  ) {
    return false
  }

  return (
    previous.start.epoch_millis !== next.start.epoch_millis ||
    previous.end.epoch_millis !== next.end.epoch_millis ||
    previous.allDay !== next.allDay ||
    previous.timeZone !== next.timeZone ||
    previous.endTimeZone !== next.endTimeZone
  )
}

export function prepareUpdateItineraryEventAction(
  changeProps: UpdateItineraryEventActionInput
): PreparedUpdateItineraryEventAction {
  const originalEvent = changeProps.event
  const previous = {
    place: originalEvent.place,
    endPlace: originalEvent.end_place,
    start: originalEvent.start_at,
    end: originalEvent.end_at,
    allDay: originalEvent.all_day,
    timeZone: originalEvent.time_zone,
    endTimeZone: originalEvent.end_time_zone,
    travelers: originalEvent.travelers,
    type: originalEvent.type,
    title: originalEvent.title,
    user_note: originalEvent.user_note,
    scheduled: originalEvent.scheduled,
    properties:
      "properties" in originalEvent ? originalEvent.properties : undefined,
  }

  const requestedType = changeProps.newType || changeProps.newPlace?.type
  const nextStart = changeProps.newStart || previous.start
  let nextEnd = changeProps.newEnd || previous.end
  const hasExplicitNextEnd = changeProps.newEnd != null
  const nextStartTimeZone = changeProps.newTimeZone || originalEvent.time_zone

  if (
    originalEvent.type === "hotel" &&
    present(requestedType) &&
    requestedType !== "hotel" &&
    !hasExplicitNextEnd
  ) {
    nextEnd = defaultActivityEndTime(
      getDayObject(nextStart, originalEvent.time_zone),
      originalEvent.time_zone
    )
  } else if (
    originalEvent.type !== "hotel" &&
    present(requestedType) &&
    requestedType === "hotel" &&
    !hasExplicitNextEnd
  ) {
    nextEnd = defaultHotelCheckOutTime(
      addDays(getDayObject(nextStart, nextStartTimeZone), 1),
      nextStartTimeZone
    )
  }

  return {
    event: originalEvent,
    previous,
    next: {
      place: changeProps.newPlace ?? previous.place,
      endPlace: changeProps.newEndPlace ?? previous.endPlace,
      start: nextStart,
      end: nextEnd,
      allDay:
        changeProps.newAllDay != null ? changeProps.newAllDay : previous.allDay,
      timeZone: changeProps.newTimeZone || previous.timeZone,
      endTimeZone: changeProps.newEndTimeZone || previous.endTimeZone,
      travelers: changeProps.newTravelers || previous.travelers,
      type: requestedType || previous.type,
      title: changeProps.newTitle || previous.title,
      user_note: changeProps.newUserNote ?? previous.user_note,
      properties:
        changeProps.newProperties != null
          ? changeProps.newProperties
          : previous.properties,
    },
    requestedScheduled: changeProps.newScheduled,
    updateTripTimeFrame: changeProps.updateTripTimeFrame,
  }
}
